import React, { useRef, useState, useMemo, useEffect } from 'react'
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import useAsyncEffect from 'use-async-effect';
import { loading } from '../../actions/Loading';
import api from '../../api/api';
import { useDispatch, useSelector } from 'react-redux';
import { List, ContactListSummary } from '../../model/List';
import { IGlobalState } from '../../model/IGlobalState';
import { HasAdminRole } from '../../model/Roles';
import VirtualizedStatsTable from '../campaignStats/VirtualizedStatsTable';
import AnimatedSaveButton from '../AnimatedSaveButton';
import AlertEx from '../AlertEx';
import ConfirmEx from '../ConfirmEx';
import { ContactListAction } from '../../model/UpdateContactListRequest';
import moment from 'moment'

interface IProps {
    stockId: string;
}

const Style = styled.div`
margin: 1rem 0;

.flex {
    display: flex;
    justify-content: space-between;
}
table {
    margin-top: 0.5rem;
}
.sample-file {
    font-size:0.65rem;
    font-style: italic;
}
.tag-segmentation-notice {
    background: #fff8e1;
    border: 1px solid #ffe58f;
    border-radius: 4px;
    padding: 0.6rem 0.85rem;
    margin: 0.5rem 0;
    font-size: 0.85rem;
    line-height: 1.45;
    color: #5c4b00;
}
.tag-segmentation-notice .detail {
    margin-top: 0.25rem;
    padding-left: 0.5rem;
}
.tag-segmentation-notice code {
    background: #fbe9a6;
    padding: 0 0.25rem;
    border-radius: 3px;
}
.contacts-filter {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 8px 0;
}
.contacts-filter input {
    padding: 6px 10px;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-size: 0.85rem;
    width: 320px;
    max-width: 100%;
}
.contacts-filter__count {
    font-size: 0.8rem;
    color: #666;
}

`;

// Tags written by the add-subscriber widget that activate per-campaign send segmentation.
const SEGMENTATION_TAGS = ['announcements', 'other-news'];

const getHeaders = () =>  {
    return [ 'Email', 'Name', 'Status', 'Tags', 'Created' ];
}

const ContactComponent: React.FC<IProps> = (props: IProps) => {

    const dispatch = useDispatch();
    const user = useSelector((state: IGlobalState) => state.user);
    const isAdmin = HasAdminRole(user);
    const [ item, setItem ] = useState<List>();
    const [ lists, setLists ] = useState<ContactListSummary[]>([]);
    const [ listId, setListId ] = useState<string>();
    const [ canUpload, setCanUpload ] = useState<boolean>(false);
    const [ query, setQuery ] = useState('');
    // the Status ">>" action menu. Rendered through a portal (see below) so it isn't clipped by
    // the virtualized rows' overflow:hidden; we track the anchored subscriber + viewport coords.
    const [ actionMenu, setActionMenu ] = useState<{ subscriber: any; top: number; left: number } | null>(null);
    const actionMenuRef = useRef<HTMLDivElement | null>(null);


    const fileInput = React.createRef<any>();
    const contactCsvFile = useRef<FileList | null>(null);
    const onFileChange = (selectorFiles: FileList|null) =>   {
        contactCsvFile.current = selectorFiles;
        setCanUpload(contactCsvFile != null && contactCsvFile.current != null && contactCsvFile.current.length > 0);
    }     


    const uploadCsv = async () : Promise<boolean> => {
        let file = contactCsvFile!.current![0];
        const result = await api().uploadContactCsv({
            file,
            stockId: props.stockId,
            listId
        });
        if (result.errorMessage) {
            AlertEx(`${result.errorMessage}`);
        } else {
            await refreshContacts();
        }
        return true;
    }

    const addNewList = async () => {
        const name = prompt('Enter new list name:');
        if (!name || !name.trim()) {
            return;
        }
        const result = await api().createContactList(props.stockId, name.trim());
        if (result.errorMessage) {
            AlertEx(result.errorMessage);
            return;
        }
        setLists(await api().getContactLists(props.stockId));
        setListId(result.id);
    }

    const addSubscriber = async () => {
        const email = prompt('Enter subscriber email address:');
        if (!email || !email.trim()) {
            return;
        }

        const name = prompt('Enter subscriber name:');
        if (!name || !name.trim()) {
            return;
        }

        // Optional tags (comma or semicolon separated). Segmentation tags: announcements, other-news.
        const tagsInput = prompt(`Enter tags (optional, comma-separated).\nFor email-alert segmentation use: ${SEGMENTATION_TAGS.join(', ')}`);
        const tags = (tagsInput || '')
            .split(/[,;]/)
            .map(t => t.trim())
            .filter(t => t.length > 0);

        // Split name into first and last (first word = first name, rest = last name)
        const nameParts = name.trim().split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';

        // Create a CSV with the email, name and tags (tags joined with ';' to keep them in one CSV cell)
        const csvContent = `Email,LastName,FirstName,Status,Tags\n${email.trim()},${lastName},${firstName},subscribed,${tags.join(';')}`;
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const file = new File([blob], 'subscriber.csv', { type: 'text/csv' });

        // Create a FileList-like object
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(file);
        contactCsvFile.current = dataTransfer.files;

        // Upload the CSV
        await uploadCsv();

        // Clear the file input
        contactCsvFile.current = null;
        setCanUpload(false);
    }

    const updateContacts = async (email: string, action: ContactListAction) => {
        const result = await api().updateContactList({
            stockId: props.stockId,
            listId,
            action,
            contacts: [{ email }]
        });
        if (result.errorMessage) {
            AlertEx(`${result.errorMessage}`);
            return;
        }
        await refreshContacts();
    }

    const markAsUnsubscribed = async (email: string) => {
        if (!confirm(`Mark ${email} as unsubscribed?`)) {
            return;
        }
        await updateContacts(email, ContactListAction.Unsubscribe);
    }

    const deleteContact = async (email: string) => {
        if (await ConfirmEx(`Delete ${email}? This cannot be undone.`)) {
            await updateContacts(email, ContactListAction.Delete);
        }
    }

    // Reloads the current list's contacts and the per-list subscriber counts.
    const refreshContacts = async () => {
        const [ list, ls ] = await Promise.all([
            api().getContactList(props.stockId, listId),
            api().getContactLists(props.stockId)
        ]);
        setItem(list);
        setLists(ls);
    }

    const refreshList = async () => {
        dispatch(loading(true));
        await refreshContacts();
        dispatch(loading(false));
    }

    useAsyncEffect(async (isMounted:()=>boolean)=> {

        dispatch(loading(true));
        const ls = await api().getContactLists(props.stockId);
        if(isMounted()){
            setLists(ls);
            setListId((ls.find(x => !x.name) || ls[0])?.id);
        }
        dispatch(loading(false));

      }, []);

    useAsyncEffect(async (isMounted:()=>boolean)=> {

        if (!listId) return;
        dispatch(loading(true));
        const result = await api().getContactList(props.stockId, listId);
        if(isMounted()){
            setItem(result)
        }
        dispatch(loading(false));

      }, [listId]);

    // Dismiss the action menu on outside click, scroll (incl. the inner virtualized list) or resize,
    // since it's a fixed-position portal anchored to a row that can move out from under it.
    useEffect(() => {
        if (!actionMenu) return;
        const close = (e: Event) => {
            if (actionMenuRef.current && e.target instanceof Node && actionMenuRef.current.contains(e.target)) return;
            setActionMenu(null);
        };
        document.addEventListener('mousedown', close);
        window.addEventListener('scroll', close, true);
        window.addEventListener('resize', close);
        return () => {
            document.removeEventListener('mousedown', close);
            window.removeEventListener('scroll', close, true);
            window.removeEventListener('resize', close);
        };
    }, [actionMenu]);

      // each row: [email, name, status, tagsDisplay, created(ISO string), subscriberModel]
      // Created stays the raw ISO-8601 string so the table sorts it chronologically (ISO sorts
      // lexicographically); the cellFormatter renders it as 'DD MMM YY' for display. (Formatting it
      // here instead was the bug - 'DD MMM YY' does NOT sort chronologically.)
      const data = useMemo<any[][]>(() => {
          if (!item) return [];
          return item.subscribers.map(subscriber => ([
              subscriber.email,
              subscriber.name,
              subscriber.status,
              (subscriber.tags && subscriber.tags.length ? subscriber.tags.join(', ') : ''),
              subscriber.created,
              subscriber, // full model object as the last element
          ]));
      }, [item]);

      // client-side filter on email (row[0]) or name (row[1]); virtualization keeps only the visible
      // rows in the DOM, so native Ctrl+F can't find off-screen contacts.
      const filtered = useMemo(() => {
          const q = query.trim().toLowerCase();
          return q
              ? data.filter(r => String(r[0]).toLowerCase().includes(q) || String(r[1]).toLowerCase().includes(q))
              : data;
      }, [data, query]);

      const presentSegmentationTags = item
          ? SEGMENTATION_TAGS.filter(tag => item.subscribers.some(s => s.tags?.some(t => t.toLowerCase() === tag)))
          : [];

      const cellFormatter = (columnName: string, cellData: any, subscriber: any) => {
          // Status column with action menu trigger
          if (columnName === 'Status') {
              // Available actions: Mark as Unsubscribed (subscribed contacts) and Delete (admin only).
              const hasActions = subscriber?.status === 'subscribed' || isAdmin;
              return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span>{cellData}</span>
                      {hasActions && (
                      <a
                          href="#"
                          onClick={(e) => {
                              e.preventDefault();
                              const rect = e.currentTarget.getBoundingClientRect();
                              setActionMenu(prev => prev?.subscriber === subscriber
                                  ? null
                                  : { subscriber, top: rect.bottom + 2, left: rect.left });
                          }}
                          style={{ cursor: 'pointer', fontWeight: 'bold' }}
                      >
                          &gt;&gt;
                      </a>
                      )}
                  </div>
              );
          }
          // Created is the raw ISO string (kept raw so it sorts chronologically) - format for display.
          if (columnName === 'Created') {
              return cellData ? moment(cellData).format('DD MMM YY') : '';
          }
          return null;
      }

    const downloadCSV = () => {
  const csvContent = `Email,LastName,FirstName,Status,Tags
john.smith@example.com,Smith,John,subscribed,
investor1@example.com,Johnson,Sarah,subscribed,linkedin;newsletter`;

  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'subscribers.csv';

  link.click();

  window.URL.revokeObjectURL(url);
};

    // Quote fields containing comma/quote/newline per RFC 4180 so names/tags can't break the layout.
    const csvField = (value: string) => {
        const s = value ?? '';
        return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };

    // Export the full list in the same shape the importer accepts (Email,LastName,FirstName,Status,Tags).
    // Mirrors the Add Subscriber split: first word = first name, the rest = last name; tags joined with ';'.
    const exportCsv = () => {
        if (!item) return;
        const rows = item.subscribers.map(s => {
            const parts = (s.name || '').trim().split(' ');
            const firstName = parts[0] || '';
            const lastName = parts.slice(1).join(' ') || '';
            const tags = (s.tags || []).join(';');
            return [s.email, lastName, firstName, s.status, tags].map(csvField).join(',');
        });
        const csvContent = ['Email,LastName,FirstName,Status,Tags', ...rows].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${(props.stockId || 'contacts').replace(/[^\w]+/g, '-')}-contacts.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
    };

    return <Style >
        <div className='flex'>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <h2>Contacts</h2>
                { lists.length > 1 &&
                <select value={listId} onChange={e => setListId(e.target.value)}>
                    { lists.map(l => <option key={l.id} value={l.id}>{l.name || '[Default]'} - {l.numSubscribers} subscriber{l.numSubscribers === 1 ? '' : 's'}</option>) }
                </select>}
            </div>
            <div>
                <div className="form-item">
                    { !canUpload && <label htmlFor="">Select Contact CSV file</label>}

                    { canUpload && <AnimatedSaveButton onClick={uploadCsv} startingText="Start Upload" progressText="Uploading..." completedText="Uploaded!"  />}
                    <input type="file" accept=".csv"
                        ref={fileInput}
                        onChange={(e) => onFileChange(e.target.files)}
                    />
                    <button className='button-sm link button-as-link sample-file' onClick={downloadCSV}>Download Sample CSV</button>
                </div>

            </div>


            </div>
        { !!item && <>
        { presentSegmentationTags.length > 0 && (
            <div className='tag-segmentation-notice'>
                <strong>Email-alert segmentation is active for this list.</strong>
                <div className='detail'>ASX announcement emails are sent only to subscribers tagged <code>announcements</code>,</div>
                <div className='detail'>News/Other emails only to those tagged <code>other-news</code>.</div>
                <div className='detail'>Subscribers without the matching tag are excluded from that send.</div>
            </div>
        )}
        <div className='flex'>
            <div></div>
            <div>
                <button className='button-sm' onClick={refreshList} style={{ marginRight: '0.5rem' }}>Refresh</button>
                <button className='button-sm' onClick={exportCsv} style={{ marginRight: '0.5rem' }}>Export</button>
                <button className='button-sm' onClick={addSubscriber} style={{ marginRight: '0.5rem' }}>Add Subscriber</button>
                <button className='button-sm' onClick={addNewList}>Add New List</button>
            </div>
        </div>
        <div className='contacts-filter'>
            <input
                type="text"
                placeholder="Filter by email or name..."
                value={query}
                onChange={e => setQuery(e.target.value)}
            />
            <span className='contacts-filter__count'>Showing {filtered.length.toLocaleString()} of {data.length.toLocaleString()}</span>
        </div>
        <VirtualizedStatsTable
            headers={getHeaders()}
            data={filtered}
            cellFormatter={cellFormatter}
            gridColumns='minmax(240px, 1.6fr) minmax(140px, 1fr) 150px minmax(120px, 1fr) 110px'
        />
        { actionMenu && createPortal(
            <div
                ref={actionMenuRef}
                style={{
                    position: 'fixed',
                    top: actionMenu.top,
                    left: actionMenu.left,
                    whiteSpace: 'nowrap',
                    backgroundColor: 'white',
                    border: '1px solid #ccc',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '3px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    zIndex: 1000,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.25rem'
                }}
            >
                { actionMenu.subscriber?.status === 'subscribed' && (
                    <a
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            const email = actionMenu.subscriber.email;
                            setActionMenu(null);
                            markAsUnsubscribed(email);
                        }}
                    >
                        Mark as Unsubscribed
                    </a>
                )}
                { isAdmin && (
                    <a
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            const email = actionMenu.subscriber.email;
                            setActionMenu(null);
                            deleteContact(email);
                        }}
                        style={{ color: '#c0392b' }}
                    >
                        Delete
                    </a>
                )}
            </div>,
            document.body
        )}
        </>}
    </Style>
}

export default ContactComponent;